import axios from "axios";
import { CSSProperties, PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";
import { CommonProps } from "../common-props";
import { CompareResultView } from "./compare-v2/compare-result-view";
import { PdfDocumentProxy } from "./compare-v2/pdf-page";
import { CompareResultV2, isCompareResultV2 } from "./compare-v2/types";

// ── Types ────────────────────────────────────────────────────────────────────

interface ComparisonSummary {
  totalChanges: number;
  additions: number;
  deletions: number;
  modifications: number;
  unchangedCount: number;
  totalCount: number;
  similarityPercent: number;
}

interface FormatChange {
  location: string | null;
  property: string; // "bold" | "italic" | "underline" | "fontSize" | "fontFamily" | "color"
  oldValue: string | null;
  newValue: string | null;
  context: string | null;
}

interface ComparisonResult {
  documentId: string;
  documentName: string;
  mimeType: string;
  oldVersionId: string;
  newVersionId: string;
  oldVersion: number;
  newVersion: number;
  oldLanguage: string | null;
  newLanguage: string | null;
  comparisonMode:
    | "collabora"
    | "textDiff"
    | "cellDiff"
    | "slideDiff"
    | "pageDiff"
    | "pdfVisualDiff"
    | "scanPdfDiff"
    | "imageDiff"
    | "docxExport"
    | "unsupported"
    | "identical"
    | "error";
  summary: ComparisonSummary | null;
  chunks: DiffChunk[] | null;
  imageDiff: {
    oldWidth: number;
    oldHeight: number;
    newWidth: number;
    newHeight: number;
    similarityPercent: number;
    dimensionsMatch: boolean;
  } | null;
  formatChanges: FormatChange[] | null;
  message: string | null;
  // `scanDiff` (v1 `CanonicalDiffResult`) alanı F10 cutover'ında KALDIRILDI. Yerini
  // `compareV2` aldı. Alanı bırakıp null doldurmak daha "uyumlu" görünürdü ama tüketiciye
  // "bu mod artık bir şey döndürmüyor" yalanını söylerdi; yokluğu derleme zamanında hata
  // verir ve tüketici v2'ye taşınmak zorunda kalır.
  /**
   * Karşılaştırma sonucu — `scanPdfDiff` modunun TEK çıktısı (`schemaVersion` "2.x").
   *
   * v1'in temsil edemediği dört şeyi taşıyor: madde bazlı özet (`clauses` — "hangi madde
   * değişti"), taşımalar (`moves` — "bu paragraf taşındı"), her bulgunun güven dökümü
   * (`confidence.factors` — "neden şüpheli") ve imza/kaşe bölgeleri (`signatures`)
   * kararlarıyla birlikte.
   *
   * `null` ise karşılaştırma başarısız olmuştur; `comparisonMode` `"error"` ve `message`
   * sebebi taşır — sessiz boş sonuç yok.
   */
  compareV2?: CompareResultV2 | null;
}

// NOT: `ScanSeverityBreakdown` / `ScanMagnitudeBreakdown` arayuzleri ve onlari tarif eden
// yorum KALDIRILDI (plan 0.4 / B7). Ikisi de v1 artigiydi: hicbir yerden okunmuyorlardi ve
// yorum artik var olmayan bir C# tipini (`CanonicalDecorativeRegionPresence`) tarif ediyordu.
// Okuyan yanlis bir mimari resmi aliyordu.

interface DiffChunk {
  type: "unchanged" | "added" | "deleted" | "modified" | "separator";
  oldLineStart: number | null;
  newLineStart: number | null;
  content: string | null;
  oldContent: string | null;
}

interface CollaboraComparisonConfig {
  newVersionId: string;
  oldVersionId: string;
  oldVersionDownloadUrl: string;
  oldVersionAccessToken: string;
  oldVersionNumber: number;
  oldVersionLanguage: string | null;
  newVersionDocumentId: string;
  newVersionNumber: number;
  newVersionLanguage: string | null;
}

interface ProviderSettings {
  collabora: { enabled: boolean; serverUrl: string };
  onlyoffice: { enabled: boolean; documentServerUrl: string };
}

// NOT: `btnStyle` ve `LegendDot` KALDIRILDI — F10 cutover'inda kaldirilan scan
// toolbar'inin yardimcilariydi ve o gunden beri hicbir yerden cagrilmiyorlardi.

// ── Provider settings cache ──────────────────────────────────────────────────

let _cachedSettings: ProviderSettings | null = null;

async function fetchSettings(): Promise<ProviderSettings> {
  if (_cachedSettings) return _cachedSettings;
  try {
    const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
    const r = await axios.get(`${base}/documentmanagement/settings`, { timeout: 5_000 });
    _cachedSettings = {
      collabora: { enabled: !!r.data?.collabora?.enabled, serverUrl: r.data?.collabora?.serverUrl ?? "" },
      onlyoffice: {
        enabled: !!r.data?.onlyOffice?.enabled,
        documentServerUrl: r.data?.onlyOffice?.documentServerUrl ?? ""
      }
    };
  } catch {
    _cachedSettings = {
      collabora: { enabled: false, serverUrl: "" },
      onlyoffice: { enabled: false, documentServerUrl: "" }
    };
  }
  return _cachedSettings;
}

// ── Component ────────────────────────────────────────────────────────────────

interface DocumentCompareProps {
  /** KDocumentVersion.Id — left (old) version. Set via SetValueOf. */
  leftVersionId?: string;
  /** KDocumentVersion.Id — right (new) version. Set via SetValueOf. */
  rightVersionId?: string;
  /**
   * Internal: fired whenever an exposed output value (leftVersionId /
   * rightVersionId) changes. The code generator wires this to a host-screen
   * re-render so components binding directly to these values refresh.
   */
  onExposedValueChange?: () => void;
  /** DOCX/DOC/ODT engine: auto | collabora | diffplex */
  /**
   * DOCX karsilastirma motoru. Liste `DocumentComparisonService.CompareAsync`in
   * gercekten ele aldigi degerlerden geliyor — daha once bu union uc deger
   * tasiyordu, designer metadata'si BESKA bes deger sunuyordu ve `collabora`
   * serviste ele alindigi halde designer'da SECILEMIYORDU. Uc liste artik ayni.
   */
  docxEngine?: "auto" | "collabora" | "diffplex" | "docxexport" | "libreoffice" | "wmlcomparer";
  /** XLSX/XLS/ODS engine: auto | deep | diffplex */
  xlsxEngine?: "auto" | "deep" | "diffplex";
  /** PDF engine: auto | pdfpage | pdfvisual | diffplex */
  pdfEngine?: "auto" | "pdfpage" | "pdfvisual" | "diffplex";
  /** PPTX/PPT/ODP engine: auto | diffplex */
  pptxEngine?: "auto" | "diffplex";
  /** TXT/CSV/JSON/XML/HTML engine: auto | diffplex */
  textEngine?: "auto" | "diffplex";
  /** Scan-aware OCR compare: auto | scan | off. When "scan", forces OcrEnrichment pipeline for any PDF/DOCX input. */
  scanEngine?: "auto" | "scan" | "off";
  /** Container inline styles. */
  style?: CSSProperties;
  /**
   * Karşılaştırma panelinin krom teması. **Varsayılan `light`.**
   *
   * Bir sürüm boyunca koyu krom koda gömülüydü ve bu yanlıştı: bu bir Kuika elementi,
   * müşterinin uygulamasının teması içinde yaşıyor ve ona uymak zorunda. Koyu tema hâlâ
   * mümkün ama İSTENEREK seçiliyor.
   *
   * Renk ve font tercihi için ayrı bir prop gerekmiyor: styling panelinin `style` değerleri
   * (`color`, `backgroundColor`, `fontFamily`, `fontSize`) panelin krom token'larına
   * yönlendiriliyor ve bütün yüzeyleri birlikte değiştiriyor.
   */
  compareTheme?: "light" | "dark";
  /** UI locale. Defaults to "tr". */
  locale?: "tr" | "en";
  /** Called when comparison finishes loading. */
  onLoad?: (result: ComparisonResult) => void;
  /** Called on error. */
  onError?: (error: Error) => void;
}

interface DocumentCompareState {
  loading: boolean;
  error: string | null;
  result: ComparisonResult | null;
  collaboraConfig: CollaboraComparisonConfig | null;
  collaboraIframeUrl: string | null;
  settings: ProviderSettings;
  collaboraComparisonSent: boolean;
  currentChangeIndex: number;
  oldImageBlobUrl: string | null;
  newImageBlobUrl: string | null;
  exportingPdf: boolean;
  /** Rapor uretimi basarisiz oldugunda kullaniciya gosterilen mesaj. */
  exportError: string | null;
  // scanPdfDiff canvas rendering
  scanLoading: boolean;
  scanError: string | null;
  scanPdfsLoaded: boolean;
  // `scanZoom` KALDIRILDI (F10 artığı): yalnızca silinen tarama gövdesinin canvas
  // ölçeklemesi için vardı. v2 paneli kendi PdfPageCanvas'ını device-pixel-ratio ile
  // ölçekliyor ve zoom denetimi sunmuyor.
  // Karakter düzeyi diff popover'ı KALDIRILDI (F10): v1'in token `charDiff` segmentlerinden
  // besleniyordu. v2 karşılığı bulgu satırındaki eski/yeni metin çifti; karakter kırılımı
  // yeniden gerekirse tel sözleşmesine ÖLÇÜLEREK eklenir, tahminle değil.
  /** Sprint 7 — Sidebar change-list severity filter. "all" shows every change;
   *  individual severities narrow the list. The filter operates on line-level severity
   *  (max of token severities) so a critical-numeric token anywhere in the line keeps
   *  the line visible under the "critical-numeric" filter. */
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

class DocumentCompareInner extends PureComponent<DocumentCompareProps & CommonProps, DocumentCompareState> {
  private _iframeRef: HTMLIFrameElement | null = null;
  private _collaboraSent = false;
  private _abortController: AbortController | null = null;
  private _collaboraTimeout: number | null = null;

  // FIX 1 & 5: Scroll sync flag and explicit panel refs
  private _isScrollSyncing = false;
  private _leftPanelRef: HTMLDivElement | null = null;
  private _rightPanelRef: HTMLDivElement | null = null;

  // scanPdfDiff rendering state (pdfjs-dist, lazy loaded)
  private _pdfjsLib: any = null;
  private _oldPdfDoc: any = null;
  private _newPdfDoc: any = null;
  private _scanLoadToken: number = 0;
  private _scanOldViewport: any = null;
  private _scanNewViewport: any = null;
  // In-flight pdfjs RenderTask handles — cancelled before starting a new render on same canvas.
  private _scanOldRenderTask: any = null;
  private _scanNewRenderTask: any = null;
  // Cross-side render-size normalization. Mixed-format compares (e.g. DOCX→PDF on the
  // left, native PDF on the right) often have different intrinsic page widths in pt:
  // Word's A4 ≈ 595, image-derived PDFs may be much wider/narrower. Without
  // normalization the same `scanZoom` produces visually different sizes per side.
  // We pick the larger of the two sides' page-1 native widths as a shared target and
  // upscale the smaller side to match — pdfjs renders vector content so upscaling is
  // free. `null` until both PDFs are loaded; per-page nativeW is measured at render
  // time so multi-page docs with mixed page sizes still align.
  private _scanGlobalTargetWidth: number | null = null;

  constructor(props: DocumentCompareProps & CommonProps) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      result: null,
      collaboraConfig: null,
      collaboraIframeUrl: null,
      settings: _cachedSettings ?? {
        collabora: { enabled: false, serverUrl: "" },
        onlyoffice: { enabled: false, documentServerUrl: "" }
      },
      collaboraComparisonSent: false,
      currentChangeIndex: -1,
      oldImageBlobUrl: null,
      newImageBlobUrl: null,
      exportingPdf: false,
      exportError: null,
      scanLoading: false,
      scanError: null,
      scanPdfsLoaded: false
    };
  }

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _overrideLeftVersionId: string | null = null;
  private _overrideRightVersionId: string | null = null;

  async componentDidMount() {
    if ((window as any)?.kuika?.isDesignTime) return;
    const settings = await fetchSettings();
    this.setState({ settings });
    window.addEventListener("message", this.handlePostMessage);
    window.addEventListener("keydown", this.handleKeyDown);
    this.scheduleCompare();
  }

  componentDidUpdate(prev: DocumentCompareProps & CommonProps, prevState: DocumentCompareState) {
    if (prev.leftVersionId !== this.props.leftVersionId || prev.rightVersionId !== this.props.rightVersionId) {
      this._collaboraSent = false;
      this.scheduleCompare();
    }
    // `scanPdfDiff` modunda iki PDF'i ayrıştır — v2 panelinin sayfa önizlemesi bunlara
    // bağlı ve panel kendisi indirmiyor (ağ çağrıları servis katmanından geçiyor).
    //
    // **Neden burada ve neden bir koruma var.** F10 cutover'ında bu tetikleyici
    // KAYBOLDU: eskiden `renderScanPdfDiff`'in içinde render sırasında çağrılıyordu ve o
    // metot silindi. Sonuç sessiz bir yetenek ölümüydü — `_oldPdfDoc`/`_newPdfDoc` null
    // kalıyor, panele `firstDocument={null}` gidiyor, önizleme hiç çizilmiyor ve
    // "sayfada göster" bağlantıları hiçbir şey yapmıyor. Hiçbir test bunu yakalamadı
    // çünkü ölçüm harness'ı arayüzü hiç çalıştırmıyor.
    //
    // Tetikleyici artık render'da değil yaşam döngüsünde: render sırasında yan etki
    // başlatmak, aynı karenin iki kez çizilmesinde çift indirme demekti. Koruma
    // `scheduleCompare()`'in sıfırladığı üç bayrak — her yeni sonuçta tekrar yükleniyor,
    // aynı sonuç için bir kez.
    // Kosul, PANELIN CIZILDIGI kosulla ayni olmak ZORUNDA.
    //
    // Eskiden `comparisonMode === "scanPdfDiff"` idi ama panel (`renderCompareV2Panel`)
    // mod yonlendirmesinin YANINDA kosulsuz ciziliyor ve yalnizca `compareV2`nin varligina
    // bakiyor. Sonuc: scanPdfDiff DISINDAKI her modda panel aciliyor, PDF'ler hic
    // yuklenmiyor ve kullanici "sayfa goruntuleri yuklenmedi" goruyor — hangi modu secerse
    // secsin. Iki kosulun ayri olmasi tam olarak bu sinifin hatasi.
    if (
      shouldLoadPageImages({
        hasCompareV2: isCompareResultV2(this.state.result?.compareV2),
        loading: this.state.scanLoading,
        error: this.state.scanError,
        loaded: this.state.scanPdfsLoaded
      })
    ) {
      void this.loadScanPdfs();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.handlePostMessage);
    window.removeEventListener("keydown", this.handleKeyDown);
    if (this._abortController) this._abortController.abort();
    if (this._collaboraTimeout) clearTimeout(this._collaboraTimeout);
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    // Revoke blob URLs
    if (this.state.oldImageBlobUrl) URL.revokeObjectURL(this.state.oldImageBlobUrl);
    if (this.state.newImageBlobUrl) URL.revokeObjectURL(this.state.newImageBlobUrl);
  }

  /** Debounced comparison trigger — waits 50ms for both left/right to be set before comparing. */
  private scheduleCompare() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(async () => {
      this._debounceTimer = null;
      // Ensure settings are loaded before comparing (setValueOf may fire before componentDidMount completes)
      if (!this.state.settings.collabora.enabled && !this.state.settings.onlyoffice.enabled) {
        const settings = await fetchSettings();
        this.setState({ settings });
      }
      const left = this._overrideLeftVersionId || this.props.leftVersionId;
      const right = this._overrideRightVersionId || this.props.rightVersionId;
      if (left && right && GUID_RE.test(left) && GUID_RE.test(right)) {
        this.loadComparison(left, right);
      }
    }, 50);
  }

  /** Called imperatively via setValueOf(componentName, versionId, "leftVersionId").
   *  When null is passed, clears the left side and resets the comparison. */
  public setLeftVersionId(versionId: string | null | undefined) {
    this._overrideLeftVersionId = versionId || null;
    this.props.onExposedValueChange?.();
    if (!versionId) {
      this.clearComparison();
      return;
    }
    this.scheduleCompare();
  }

  /** Called imperatively via setValueOf(componentName, versionId, "rightVersionId").
   *  When null is passed, clears the right side and resets the comparison. */
  public setRightVersionId(versionId: string | null | undefined) {
    this._overrideRightVersionId = versionId || null;
    this.props.onExposedValueChange?.();
    if (!versionId) {
      this.clearComparison();
      return;
    }
    this.scheduleCompare();
  }

  /** Resets the comparison to empty state. */
  private clearComparison() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this.setState({
      result: null,
      error: null,
      loading: false,
      collaboraConfig: null,
      collaboraIframeUrl: null,
      collaboraComparisonSent: false,
      currentChangeIndex: 0,
      oldImageBlobUrl: null,
      newImageBlobUrl: null,
      exportingPdf: false,
      exportError: null
    });
  }

  // ── Public getters (symbol picker) ───────────────────────────────────────

  /** Getter for symbol picker — returns the current left version ID. */
  public getLeftVersionId(): string | null {
    return this._overrideLeftVersionId || this.props.leftVersionId || null;
  }

  /** Getter for symbol picker — returns the current right version ID. */
  public getRightVersionId(): string | null {
    return this._overrideRightVersionId || this.props.rightVersionId || null;
  }

  // ── Scroll sync ────────────────────────────────────────────────────────────

  private syncScroll = (source: HTMLDivElement, target: HTMLDivElement) => {
    if (this._isScrollSyncing) return;
    this._isScrollSyncing = true;
    target.scrollTop = source.scrollTop;
    requestAnimationFrame(() => {
      this._isScrollSyncing = false;
    });
  };

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────

  private handleKeyDown = (e: KeyboardEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    if (e.key === "n" || e.key === "ArrowDown") {
      e.preventDefault();
      this.navigateChange(1);
    } else if (e.key === "p" || e.key === "ArrowUp") {
      e.preventDefault();
      this.navigateChange(-1);
    }
  };

  /** Returns indices of changed chunks in the current result */
  private getChangeIndices(): number[] {
    const { result } = this.state;
    if (!result?.chunks) return [];
    const indices: number[] = [];
    result.chunks.forEach((chunk, i) => {
      if (chunk.type !== "unchanged" && chunk.type !== "separator") {
        indices.push(i);
      }
    });
    return indices;
  }

  private navigateChange = (direction: 1 | -1) => {
    const changeIndices = this.getChangeIndices();
    if (changeIndices.length === 0) return;

    const { currentChangeIndex } = this.state;
    let newIndex: number;
    if (currentChangeIndex < 0) {
      newIndex = direction === 1 ? 0 : changeIndices.length - 1;
    } else {
      newIndex = currentChangeIndex + direction;
      if (newIndex < 0) newIndex = changeIndices.length - 1;
      if (newIndex >= changeIndices.length) newIndex = 0;
    }

    this.setState({ currentChangeIndex: newIndex });
    this.scrollToChange(changeIndices[newIndex]);
  };

  private scrollToChange = (chunkIndex: number) => {
    // Each line in the diff panels has a consistent height of 22px
    const lineHeight = 22;
    const headerHeight = 32; // panel header
    const scrollTarget = chunkIndex * lineHeight - headerHeight;

    if (this._leftPanelRef) {
      this._leftPanelRef.scrollTop = Math.max(0, scrollTarget);
    }
    if (this._rightPanelRef) {
      this._rightPanelRef.scrollTop = Math.max(0, scrollTarget);
    }
  };

  // ── Data loading ────────────────────────────────────────────────────────────

  private loadComparison = async (leftVersionId: string, rightVersionId: string) => {
    if (!GUID_RE.test(leftVersionId) || !GUID_RE.test(rightVersionId)) return;

    // Cancel previous comparison request
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._collaboraSent = false;
    if (this._collaboraTimeout) {
      clearTimeout(this._collaboraTimeout);
      this._collaboraTimeout = null;
    }
    // Revoke old blob URLs
    if (this.state.oldImageBlobUrl) URL.revokeObjectURL(this.state.oldImageBlobUrl);
    if (this.state.newImageBlobUrl) URL.revokeObjectURL(this.state.newImageBlobUrl);
    // Reset canvas-side state: previous rendered PDFs belong to the prior comparison.
    this.resetScanCanvasState();

    this.setState({
      loading: true,
      error: null,
      result: null,
      collaboraConfig: null,
      collaboraIframeUrl: null,
      collaboraComparisonSent: false,
      currentChangeIndex: -1,
      oldImageBlobUrl: null,
      newImageBlobUrl: null,
      exportingPdf: false,
      exportError: null
    });

    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");

      // Step 1: Get comparison result from backend
      // Don't compute text diff if Collabora will handle comparison (saves backend CPU)
      const wantsCollabora =
        (this.props.docxEngine || "auto") === "collabora" ||
        ((this.props.docxEngine || "auto") === "auto" && this.state.settings.collabora.enabled);
      const computeTextDiff = !wantsCollabora;
      const resp = await axios.post(
        `${base}/documentmanagement/compare`,
        {
          leftVersionId,
          rightVersionId,
          computeTextDiff,
          docxEngine: this.props.docxEngine || "auto",
          xlsxEngine: this.props.xlsxEngine || "auto",
          pdfEngine: this.props.pdfEngine || "auto",
          pptxEngine: this.props.pptxEngine || "auto",
          textEngine: this.props.textEngine || "auto",
          scanEngine: this.props.scanEngine || "auto"
        },
        { signal }
      );
      const result: ComparisonResult = resp.data;

      // Step 2a: If imageDiff, fetch both versions as blob URLs for preview
      if (result.comparisonMode === "imageDiff") {
        this.setState({ result, loading: false });
        this.props.onLoad?.(result);
        this.fetchImageBlobs(leftVersionId, rightVersionId, signal);
      }
      // Step 2b: If Collabora mode and Collabora is enabled, fetch WOPI config BEFORE setting loading=false
      else if (result.comparisonMode === "collabora" && this.state.settings.collabora.enabled) {
        const colResp = await axios.post(
          `${base}/documentmanagement/compare/collabora`,
          {
            leftVersionId,
            rightVersionId
          },
          { signal }
        );
        const colConfig: CollaboraComparisonConfig = colResp.data;

        // Get Collabora iframe URL for the NEW version.
        // Must be in EDIT mode — Action_CompareDocuments modifies the doc (inserts tracked changes).
        const wopiResp = await axios.post(
          `${base}/collabora/url`,
          {
            documentId: colConfig.newVersionDocumentId,
            versionId: colConfig.newVersionId,
            action: "edit"
          },
          { signal }
        );

        // Set everything together — result + collabora config + loading=false
        this.setState({
          result,
          loading: false,
          collaboraConfig: colConfig,
          collaboraIframeUrl: (() => {
            const rawUrl = wopiResp.data?.iframeUrl ?? wopiResp.data?.url ?? null;
            if (!rawUrl) return null;
            const origin = window.location.origin;
            const sep = rawUrl.includes("?") ? "&" : "?";
            const finalUrl = `${rawUrl}${sep}postmessage_origin=*`;

            return finalUrl;
          })()
        });
        this.props.onLoad?.(result);

        // Timeout: if Collabora doesn't send comparison within 30s, show error (no fallback)
        this._collaboraTimeout = window.setTimeout(() => {
          if (!this._collaboraSent) {
            this.setState({
              error:
                this.props.locale === "tr"
                  ? "Collabora sunucusu yanıt vermedi. Lütfen Collabora bağlantısını kontrol edin veya docxEngine='diffplex' kullanın."
                  : "Collabora server did not respond. Please check Collabora connection or use docxEngine='diffplex'.",
              loading: false,
              collaboraConfig: null,
              collaboraIframeUrl: null
            });
          }
        }, 30_000);
      }
      // Step 2c: All other modes — text diff, cell diff, slide diff, etc.
      else {
        this.setState({ result, loading: false });
        this.props.onLoad?.(result);
      }
    } catch (err: any) {
      if (axios.isCancel(err)) return; // previous request cancelled, ignore
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Comparison failed";
      this.setState({ loading: false, error: msg });
      this.props.onError?.(err instanceof Error ? err : new Error(msg));
    }
  };

  // ── Collabora PostMessage ───────────────────────────────────────────────────

  private handlePostMessage = (event: MessageEvent) => {
    if (!this.state.collaboraConfig) return;

    const serverUrl = this.state.settings.collabora.serverUrl;
    if (!serverUrl) return;
    if (!event.origin.startsWith(new URL(serverUrl).origin)) return;

    let data: any;
    try {
      data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    // Collabora loading failure → show error (no fallback to text diff)
    if (data?.MessageId === "App_LoadingStatus" && data?.Values?.Status === "Failed") {
      if (this._collaboraTimeout) {
        clearTimeout(this._collaboraTimeout);
        this._collaboraTimeout = null;
      }
      this.setState({
        error:
          this.props.locale === "tr"
            ? "Collabora dokümanı yükleyemedi. Lütfen bağlantıyı kontrol edin veya docxEngine='diffplex' kullanın."
            : "Collabora failed to load the document. Please check connection or use docxEngine='diffplex'.",
        collaboraConfig: null,
        collaboraIframeUrl: null
      });
      return;
    }

    // Collabora signals it's ready to receive PostMessages
    if (data?.MessageId === "Host_PostmessageReady") {
    }

    // When Collabora finishes loading, send the comparison command
    // Wait for BOTH Document_Loaded AND a small delay to ensure PostMessage handler is ready
    if (data?.MessageId === "App_LoadingStatus" && data?.Values?.Status === "Document_Loaded") {
      if (!this._collaboraSent && this._iframeRef?.contentWindow && this.state.collaboraConfig) {
        this._collaboraSent = true;
        if (this._collaboraTimeout) {
          clearTimeout(this._collaboraTimeout);
          this._collaboraTimeout = null;
        }
        this.setState({ collaboraComparisonSent: true });

        const collaboraOrigin = new URL(this.state.settings.collabora.serverUrl).origin;
        // Collabora Action_CompareDocuments handler expects:
        //   msg.Values.url (lowercase!) — URL to fetch the old document for comparison
        //   msg.Values.filename (optional) — display name for the old document
        // The URL must include access_token as query param (WOPI auth).
        const msg = {
          MessageId: "Action_CompareDocuments",
          SendTime: Date.now(),
          Values: {
            url: this.state.collaboraConfig.oldVersionDownloadUrl,
            filename: `v${this.state.collaboraConfig.oldVersionNumber ?? "old"}.docx`
          }
        };
        // Delay: Collabora may not be ready to receive PostMessages immediately after Document_Loaded
        setTimeout(() => {
          // Use "*" as target origin to bypass any origin mismatch issues
          this._iframeRef?.contentWindow?.postMessage(JSON.stringify(msg), "*");
        }, 2000);
      }
    }
  };

  private setIframeRef = (el: HTMLIFrameElement | null) => {
    this._iframeRef = el;
  };

  public setVersions(leftVersionId: string, rightVersionId: string) {
    this._collaboraSent = false;
    this._overrideLeftVersionId = leftVersionId;
    this._overrideRightVersionId = rightVersionId;
    this.scheduleCompare();
  }

  // ── Image blob fetcher ──────────────────────────────────────────────────────

  private fetchImageBlobs = async (leftVersionId: string, rightVersionId: string, signal: AbortSignal) => {
    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const [oldResp, newResp] = await Promise.all([
        axios.get(`${base}/documentmanagement/version/${leftVersionId}/download`, {
          responseType: "blob",
          signal
        }),
        axios.get(`${base}/documentmanagement/version/${rightVersionId}/download`, {
          responseType: "blob",
          signal
        })
      ]);
      const oldUrl = URL.createObjectURL(oldResp.data);
      const newUrl = URL.createObjectURL(newResp.data);
      this.setState({ oldImageBlobUrl: oldUrl, newImageBlobUrl: newUrl });
    } catch {
      // Non-critical — image preview just won't show
    }
  };

  // ── PDF Export ──────────────────────────────────────────────────────────────

  private exportPdf = async () => {
    const left = this._overrideLeftVersionId || this.props.leftVersionId;
    const right = this._overrideRightVersionId || this.props.rightVersionId;
    if (!left || !right) return;

    this.setState({ exportingPdf: true });
    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const resp = await axios.post(`${base}/documentmanagement/compare/as-file`, {
        leftVersionId: left,
        rightVersionId: right,
        computeTextDiff: true,
        docxEngine: this.props.docxEngine || "auto",
        xlsxEngine: this.props.xlsxEngine || "auto",
        pdfEngine: this.props.pdfEngine || "auto",
        pptxEngine: this.props.pptxEngine || "auto",
        textEngine: this.props.textEngine || "auto"
      });

      const { base64Content, mimeType, fileName } = resp.data;
      if (base64Content) {
        const byteChars = atob(base64Content);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "comparison-report";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e: unknown) {
      // Hata SESSIZCE YUTULMUYOR. Eskiden `catch { /* Non-critical */ }` vardi ve
      // kullanici icin bu "butona bastim, hicbir sey olmadi"ya esitti — I6'nin panzehir
      // yazildigi desenin ta kendisi. Rapor uretmek non-critical olabilir; BASARISIZ
      // OLDUGUNU SOYLEMEMEK degil.
      const isTr = this.props.locale === "tr";
      this.setState({
        exportError: isTr
          ? "Rapor olusturulamadi. Lutfen tekrar deneyin."
          : "Could not generate the report. Please try again."
      });
      // eslint-disable-next-line no-console
      console.error("[document-compare] compare/as-file failed", e);
    } finally {
      this.setState({ exportingPdf: false });
    }
  };

  // ── Render: Similarity badge ───────────────────────────────────────────────

  private renderSimilarityBadge() {
    const { result } = this.state;
    if (!result?.summary) return null;
    const sim = result.summary.similarityPercent ?? 100;
    const color = sim >= 90 ? "#28a745" : sim >= 60 ? "#ff9800" : "#d73a49";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 700,
          color: "#fff",
          background: color
        }}
      >
        {sim.toFixed(1)}%
      </span>
    );
  }

  // ── Render: Statistics progress bar ────────────────────────────────────��───

  private renderStatsBar() {
    const { result } = this.state;
    if (!result?.summary || result.summary.totalCount === 0) return null;
    const s = result.summary;
    const total = s.totalCount || 1;
    const addPct = (s.additions / total) * 100;
    const delPct = (s.deletions / total) * 100;
    const modPct = (s.modifications / total) * 100;
    const unchPct = 100 - addPct - delPct - modPct;

    return (
      <div
        style={{
          display: "flex",
          width: 120,
          height: 6,
          borderRadius: 3,
          overflow: "hidden",
          background: "#eee"
        }}
        title={`${s.unchangedCount} unchanged, +${s.additions}, -${s.deletions}, ~${s.modifications}`}
      >
        {unchPct > 0 && <div style={{ width: `${unchPct}%`, background: "#c8e6c9" }} />}
        {addPct > 0 && <div style={{ width: `${addPct}%`, background: "#28a745" }} />}
        {modPct > 0 && <div style={{ width: `${modPct}%`, background: "#ff9800" }} />}
        {delPct > 0 && <div style={{ width: `${delPct}%`, background: "#d73a49" }} />}
      </div>
    );
  }

  // ── Render: Change list sidebar — KALDIRILDI (plan 0.4) ────────────────────
  //
  // `renderSidebar` HICBIR YERDEN cagrilmiyordu ve arkasindaki her sey oluydu:
  // severity filtre cipleri, `renderChangeList`, `renderFormatChangeList`, tablolar
  // sekmesi, `severityFilter`. F10 cutover'inda `renderScanCompare` yalnizca v2
  // panelini dondurmeye gecti ve bu ~500 satir arkada kaldi.
  //
  // Silindi, tasinmadi: v2 paneli ayni isi zaten yapiyor (bulgular / maddeler /
  // tasimalar / imzalar sekmeleri) ve sunucu-sahipli siralama ile guven/onem
  // eksenleri onda duruyor. Iki liste birakmak, esiklerin zamanla ayrismasi demekti.

  // ── Render: Design-time placeholder ─────────────────────────────────────────

  private renderPlaceholder() {
    return (
      <div
        className={this.props.className}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          minHeight: 120,
          background: "#f5f5f5",
          border: "1.5px dashed #bbb",
          borderRadius: 6,
          color: "#888",
          fontFamily: "sans-serif",
          pointerEvents: "none",
          userSelect: "none",
          boxSizing: "border-box",
          ...this.props.style
        }}
      >
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 8, opacity: 0.45 }}>
          <path
            d="M9 2H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6"
            stroke="#555"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15 2h5a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-6"
            stroke="#555"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line x1="12" y1="2" x2="12" y2="22" stroke="#555" strokeWidth="1" strokeDasharray="3 3" />
        </svg>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Document Compare</span>
        <span style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
          {this.props.locale === "tr"
            ? "Karşılaştırmak için iki versiyon ID'si bağlayın"
            : "Bind two version IDs to compare"}
        </span>
      </div>
    );
  }

  // ── Render: Collabora tracked changes ───────────────────────────────────────

  // FIX 4: Loading overlay on top of Collabora iframe
  private renderCollabora() {
    const { collaboraIframeUrl } = this.state;
    return (
      <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 800 }}>
        {!this.state.collaboraComparisonSent && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.85)",
              zIndex: 10,
              fontFamily: "sans-serif"
            }}
          >
            {this.props.locale === "tr" ? "Karşılaştırma yükleniyor..." : "Loading comparison..."}
          </div>
        )}
        {collaboraIframeUrl ? (
          <iframe
            ref={this.setIframeRef}
            src={collaboraIframeUrl}
            style={{ width: "100%", height: "100%", minHeight: 600, border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-downloads"
            title="Document Comparison"
          />
        ) : (
          <div style={{ padding: 20, color: "#888" }}>
            {this.props.locale === "tr" ? "Hazırlanıyor..." : "Preparing comparison..."}
          </div>
        )}
      </div>
    );
  }

  // ── Render: LibreOffice compare (tracked changes DOCX in iframe) ─────────────

  private renderDocxExportCompare() {
    const { result, settings } = this.state;
    const resultUrl = (result as any)?.resultDocumentUrl;
    const isTr = this.props.locale === "tr";
    if (!resultUrl) {
      return (
        <div style={{ padding: 20, color: "#888", fontFamily: "sans-serif" }}>
          {isTr ? "Karsilastirma sonucu olusturulamadi." : "Comparison result could not be generated."}
        </div>
      );
    }

    // If OnlyOffice is available, show result DOCX in OnlyOffice iframe (read-only)
    if (settings.onlyoffice.enabled && settings.onlyoffice.documentServerUrl) {
      const dsUrl = settings.onlyoffice.documentServerUrl.replace(/\/$/, "");
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 600 }}>
          <div
            style={{
              padding: "6px 12px",
              background: "#e8f5e9",
              borderBottom: "1px solid #c8e6c9",
              fontSize: 12,
              fontFamily: "sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            <span style={{ fontWeight: 600, color: "#2e7d32" }}>
              {isTr ? "Karsilastirma gorunumu (salt okunur)" : "Comparison view (read-only)"}
            </span>
            <span style={{ fontSize: 11, color: "#888" }}>
              <span style={{ color: "#008000", fontWeight: 700 }}>yesil bold</span> = {isTr ? "eklenen" : "added"},{" "}
              <span style={{ color: "#CC0000", fontStyle: "italic", textDecoration: "line-through" }}>
                kirmizi italic
              </span>{" "}
              = {isTr ? "silinen" : "deleted"}
            </span>
            <span style={{ flex: 1 }} />
          </div>
          <div
            id="onlyoffice-compare-editor"
            ref={(el) => {
              if (el && !(el as any).__ooInit) {
                (el as any).__ooInit = true;
                this.initOnlyOfficeViewer(el, dsUrl, resultUrl);
              }
            }}
            style={{ flex: 1, width: "100%" }}
          />
        </div>
      );
    }

    // If Collabora is available, show in Collabora iframe (read-only via WOPI)
    // For Collabora we'd need a WOPI endpoint for the result file — for now show download
    // with a note that OnlyOffice is recommended

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "sans-serif"
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 500 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128196;</div>
          <h3 style={{ margin: "0 0 8px", color: "#333" }}>
            {isTr ? "Karsilastirma Dokumani Hazir" : "Comparison Document Ready"}
          </h3>
          <p style={{ color: "#666", fontSize: 14, lineHeight: 1.5, margin: "0 0 20px" }}>
            {isTr
              ? "Farklar Word Track Changes formatinda isaretlenmistir. Kirmizi ustu cizili = silinen, Yesil alti cizili = eklenen. Dokumani indirip Word, OnlyOffice veya Collabora ile acabilirsiniz."
              : "Differences are marked using Word Track Changes format. Red strikethrough = deleted, Green underline = added. Download and open with Word, OnlyOffice or Collabora."}
          </p>
          <a
            href={resultUrl}
            download="comparison-result.docx"
            style={{
              display: "inline-block",
              padding: "12px 32px",
              background: "#1a73e8",
              color: "#fff",
              borderRadius: 6,
              textDecoration: "none",
              fontSize: 15,
              fontWeight: 600
            }}
          >
            {isTr ? "Karsilastirma DOCX Indir" : "Download Comparison DOCX"}
          </a>
          {!settings.onlyoffice.enabled && (
            <p style={{ marginTop: 16, fontSize: 11, color: "#888" }}>
              {isTr
                ? "OnlyOffice Document Server etkinlestirildiginde, sonuc burada dogrudan goruntulenecektir."
                : "When OnlyOffice Document Server is enabled, results will be displayed inline here."}
            </p>
          )}
        </div>
      </div>
    );
  }

  private async initOnlyOfficeViewer(container: HTMLElement, dsUrl: string, docUrl: string) {
    const isTr = this.props.locale === "tr";

    // Step 1: Get signed config from backend (same pattern as Document element)
    // Extract just the file name from the full result URL (e.g., "abc123_result.docx")
    let fileName: string;
    try {
      const urlObj = new URL(docUrl, window.location.origin);
      const pathParts = urlObj.pathname.split("/");
      fileName = decodeURIComponent(pathParts[pathParts.length - 1]);
    } catch {
      fileName = docUrl.split("/").pop()?.split("?")[0] || docUrl;
    }

    let config: any;
    try {
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const resp = await axios.post(`${base}/documentmanagement/compare/lo-result/onlyoffice-config`, {
        fileName,
        title: "Comparison Result",
        locale: this.props.locale || "en"
      });
      config = resp.data;
    } catch (err: any) {
      console.error("[Compare] Failed to get OnlyOffice config:", err);
      // Fallback: show download button
      container.innerHTML = `<div style="padding:20px;text-align:center;color:#888;font-family:sans-serif;">
        <p>${isTr ? "OnlyOffice yapilandirmasi alinamadi." : "Could not get OnlyOffice config."}</p>
        <a href="${docUrl}" download="comparison-result.docx" style="color:#1a73e8;">${isTr ? "DOCX Indir" : "Download DOCX"}</a>
      </div>`;
      return;
    }

    // Step 2: Load OnlyOffice API script
    const scriptId = "onlyoffice-api-script";
    const serverUrl = config.documentServerUrl || dsUrl;
    const apiUrl = `${serverUrl}/web-apps/apps/api/documents/api.js`;

    const initEditor = () => {
      const DocsAPI = (window as any).DocsAPI;
      if (!DocsAPI) return;

      // Build final config using backend-signed values
      const editorConfig = {
        ...config,
        width: "100%",
        height: "100%",
        type: "embedded",
        events: {
          onReady: () => {
            // Editor ready
          },
          onError: (e: any) => {
            console.error("[Compare OnlyOffice] Error:", e);
          }
        }
      };

      new DocsAPI.DocEditor(container.id, editorConfig);
    };

    if ((window as any).DocsAPI) {
      initEditor();
    } else {
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = apiUrl;
        script.onload = initEditor;
        document.head.appendChild(script);
      } else {
        // Script already loading — wait for it
        if ((window as any).DocsAPI) {
          initEditor();
        } else {
          script.addEventListener("load", initEditor);
        }
      }
    }
  }

  // ── Render: Text diff (side-by-side) ────────────────────────────────────────

  private renderTextDiff() {
    const { result } = this.state;
    if (!result?.chunks || result.chunks.length === 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#888",
            fontFamily: "sans-serif"
          }}
        >
          <span>
            {this.props.locale === "tr"
              ? "Karşılaştırılacak metin içeriği bulunamadı."
              : "No text content available for comparison."}
          </span>
        </div>
      );
    }

    const oldLines: {
      num: number | null;
      text: string;
      type: string;
      inline: { type: string; text: string }[] | null;
    }[] = [];
    const newLines: {
      num: number | null;
      text: string;
      type: string;
      inline: { type: string; text: string }[] | null;
    }[] = [];

    for (const chunk of result.chunks) {
      switch (chunk.type) {
        case "unchanged":
          oldLines.push({ num: chunk.oldLineStart, text: chunk.content ?? "", type: "unchanged", inline: null });
          newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "unchanged", inline: null });
          break;
        case "added":
          oldLines.push({ num: null, text: "", type: "empty", inline: null });
          newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "added", inline: null });
          break;
        case "deleted":
          oldLines.push({ num: chunk.oldLineStart, text: chunk.oldContent ?? "", type: "deleted", inline: null });
          newLines.push({ num: null, text: "", type: "empty", inline: null });
          break;
        case "modified": {
          // Word-level inline changes from backend
          const inl = (chunk as any).inlineChanges as { type: string; text: string }[] | null;
          if (inl && inl.length > 0) {
            // Old side: show equal + delete segments
            const oldInline = inl.filter((s) => s.type === "equal" || s.type === "delete");
            // New side: show equal + insert segments
            const newInline = inl.filter((s) => s.type === "equal" || s.type === "insert");
            oldLines.push({
              num: chunk.oldLineStart,
              text: chunk.oldContent ?? "",
              type: "modified",
              inline: oldInline
            });
            newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "modified", inline: newInline });
          } else {
            oldLines.push({ num: chunk.oldLineStart, text: chunk.oldContent ?? "", type: "deleted", inline: null });
            newLines.push({ num: chunk.newLineStart, text: chunk.content ?? "", type: "added", inline: null });
          }
          break;
        }
        case "separator":
          oldLines.push({ num: null, text: "\u00b7\u00b7\u00b7", type: "separator", inline: null });
          newLines.push({ num: null, text: "\u00b7\u00b7\u00b7", type: "separator", inline: null });
          break;
      }
    }

    /** Renders word-level inline diff segments with highlighting */
    const renderInlineSegments = (segments: { type: string; text: string }[], side: "old" | "new") => {
      return segments.map((seg, i) => {
        if (seg.type === "equal") {
          return <span key={i}>{seg.text}</span>;
        }
        if (seg.type === "delete" && side === "old") {
          return (
            <span key={i} style={{ background: "#fdb8c0", textDecoration: "line-through", borderRadius: 2 }}>
              {seg.text}
            </span>
          );
        }
        if (seg.type === "insert" && side === "new") {
          return (
            <span key={i} style={{ background: "#acf2bd", fontWeight: 600, borderRadius: 2 }}>
              {seg.text}
            </span>
          );
        }
        return null;
      });
    };

    const lineStyle = (type: string): CSSProperties => ({
      padding: "1px 8px",
      fontFamily: "monospace",
      fontSize: 13,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minHeight: 22,
      lineHeight: "22px",
      background:
        type === "added"
          ? "#e6ffed"
          : type === "deleted"
            ? "#ffeef0"
            : type === "modified"
              ? "#fff8e1"
              : type === "empty"
                ? "#fafafa"
                : type === "separator"
                  ? "#f0f0f0"
                  : "transparent",
      borderLeft:
        type === "added"
          ? "3px solid #28a745"
          : type === "deleted"
            ? "3px solid #d73a49"
            : type === "modified"
              ? "3px solid #ff9800"
              : "3px solid transparent"
    });

    const numStyle: CSSProperties = {
      width: 40,
      minWidth: 40,
      textAlign: "right",
      padding: "1px 6px",
      color: "#999",
      fontSize: 12,
      fontFamily: "monospace",
      userSelect: "none",
      lineHeight: "22px"
    };

    return (
      <div style={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
        {/* Left panel — old version */}
        <div
          ref={(el) => {
            this._leftPanelRef = el;
          }}
          style={{ flex: 1, overflow: "auto", borderRight: "1px solid #ddd" }}
          onScroll={() => {
            if (this._leftPanelRef && this._rightPanelRef) {
              this.syncScroll(this._leftPanelRef, this._rightPanelRef);
            }
          }}
        >
          <div
            style={{
              padding: "8px 0",
              background: "#fafafa",
              borderBottom: "1px solid #eee",
              fontWeight: 600,
              fontSize: 13,
              paddingLeft: 12,
              color: "#666"
            }}
          >
            {(result as any).oldDocumentName || result!.documentName} — v{result!.oldVersion}
            {result!.oldLanguage ? ` (${result!.oldLanguage})` : ""}
          </div>
          {oldLines.map((line, i) => (
            <div key={i} style={{ display: "flex" }}>
              <div style={numStyle}>{line.num ?? ""}</div>
              <div style={{ ...lineStyle(line.type), flex: 1 }}>
                {line.inline ? renderInlineSegments(line.inline, "old") : line.text}
              </div>
            </div>
          ))}
        </div>

        {/* Right panel — new version */}
        <div
          ref={(el) => {
            this._rightPanelRef = el;
          }}
          style={{ flex: 1, overflow: "auto" }}
          onScroll={() => {
            if (this._rightPanelRef && this._leftPanelRef) {
              this.syncScroll(this._rightPanelRef, this._leftPanelRef);
            }
          }}
        >
          <div
            style={{
              padding: "8px 0",
              background: "#fafafa",
              borderBottom: "1px solid #eee",
              fontWeight: 600,
              fontSize: 13,
              paddingLeft: 12,
              color: "#666"
            }}
          >
            {result!.documentName} — v{result!.newVersion}
            {result!.newLanguage ? ` (${result!.newLanguage})` : ""}
          </div>
          {newLines.map((line, i) => (
            <div key={i} style={{ display: "flex" }}>
              <div style={numStyle}>{line.num ?? ""}</div>
              <div style={{ ...lineStyle(line.type), flex: 1 }}>
                {line.inline ? renderInlineSegments(line.inline, "new") : line.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render: Summary bar ─────────────────────────────────────────────────────

  /**
   * v2 sonucunun paneli.
   *
   * **v2 varsa BİRİNCİL görünüm.** Sunucu bu modda v2'yi birincil yol yaptı çünkü ölçülen
   * her eksende v1'i geçiyor (içerik recall %100 ↔ %69,2, imza %93,9 ↔ %3,0, kesin
   * bulgularda yanlış vurgu %62,7 ↔ %91,6) ve v1'in hiç taşımadığı üç şeyi taşıyor: madde
   * bazlı diff, taşıma tespiti, güven dökümü. Panel bu yüzden geniş açılıyor ve eski gövde
   * yanında daralıyor — daha iyi sonuç elde varken kullanıcıya kötüsünü ana ekran yapmak
   * bir tercih değil, ihmaldi.
   *
   * Eski gövde SİLİNMİYOR: sayfa görüntüsünü ve vurgu katmanını hâlâ o render ediyor ve
   * v2 bir çift üzerinde başarısız olursa (`compareV2 == null`) tek görünüm o kalıyor.
   *
   * Sürüm kontrolü `schemaVersion` ile (bkz. `isCompareResultV2`) — alan varlığına bakmak,
   * sunucu yeni bir alan eklediğinde sessizce yanlış dala girmek demek olurdu.
   */
  /**
   * `scanPdfDiff` modunun TEK görünümü: v2 paneli.
   *
   * <p>F10 cutover'ında eski gövde (`renderScanPdfDiff` + `renderScanTableCard` +
   * karakter-diff popover'ı, toplam ~1.500 satır) kaldırıldı. O kod v1'in
   * sayfa→satır→token yapısını çiziyordu ve v1 motoru artık yok.</p>
   *
   * <p>Yerine v2 panelinin kendi vurgu katmanı geçti (`PdfPageCanvas`): bulgular ve İMZA
   * bölgelerini AYNI katmanda, ayrı renklerde çiziyor. Eski gövdede imza bölgesi çizimi
   * ayrı bir kod yoluydu ve v2 tel sözleşmesinde imza hiç yoktu — ikisi bu turda
   * birleştirildi.</p>
   *
   * <p>v2 yoksa HATA gösteriliyor, boş görünüm değil: motor başarısız olduğunda sebep
   * söylenmek zorunda (`ComparisonMode = "error"`, `Message` dolu).</p>
   */
  private renderScanCompare(): ReactNode {
    // v2 VARSA burada hicbir sey cizilmiyor: panel mod yonlendirmesinin yaninda kosulsuz
    // ciziliyor ve buradan da dondurmek onu IKI KEZ cizerdi.
    //
    // v2 YOKSA hata gosteriliyor, bos gorunum degil: motor basarisiz olduginda sebep
    // soylenmek zorunda (`ComparisonMode = "error"`, `Message` dolu). Bu dal yalnizca o
    // is icin duruyor.
    if (!isCompareResultV2(this.state.result?.compareV2)) return this.renderError();
    return null;
  }

  private renderCompareV2Panel(): ReactNode {
    const wire = this.state.result?.compareV2;
    if (!isCompareResultV2(wire)) return null;

    return (
      <div
        style={{
          // TEK görünüm, yan panel değil: F10 cutover'ında eski gövde kaldırıldı, dolayısıyla
          // ayıracak bir şey yok. `1 1 60%` ve soldaki kenarlık o dönemin kalıntısıydı.
          flex: 1,
          minWidth: 0,
          // YÜKSEKLİK GÖRÜNTÜLEYİCİNİN.
          //
          // Burada `overflow: auto` + `padding: 12` vardı: panel kendi içinde kayan bir
          // sayfa gibi davranıyor, belge panelleri de sabit 620 px'e sıkışıyordu. Belge ana
          // yüzeyse yüksekliği o almalı — kapsayıcı artık kaydırmıyor, panel kendi iç
          // bölgelerini kaydırıyor.
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          fontFamily: "sans-serif"
        }}
      >
        <CompareResultView
          result={wire}
          language={this.props.locale}
          /*
            Tema ve styling stili panele GEÇİYOR. Panel kendi kromunu bunlardan kuruyor:
            `style`daki renk/font krom token'larına yönleniyor, `theme` yalnızca istenerek
            koyuya alıyor. Bir önceki sürüm koyu kromu koda gömmüştü — bir Kuika elementi
            müşterinin temasını dayatamaz, ona uyar.
          */
          theme={this.props.compareTheme ?? "light"}
          style={this.props.style}
          /*
            `onNavigate` GEÇİRİLMİYOR. Eskiden buraya bir işleyici veriliyordu ve o
            işleyici `scanPageIdx`'i state'e yazıyordu — hiçbir yerden OKUNMAYAN bir
            state. Madde ve taşıma sekmelerindeki sayfa butonları tıklanıyor, hiçbir şey
            olmuyordu (plan 0.4).

            Panel artık kendi kaydırmasını yapıyor (B3: `DocumentPane`), yani ebeveynin
            takip edeceği bir sayfa indeksi yok. Ölü state ve onu besleyen işleyici
            kaldırıldı; prop opsiyonel, panel onsuz da tam çalışıyor.
          */
          /*
            Ayrıştırılmış PDF dokümanları panele GEÇİRİLİYOR, panel kendisi indirmiyor:
            proje kuralı gereği ağ çağrıları servis katmanından geçiyor ve bu bileşen iki
            tarafı zaten yüklüyor. İkinci bir indirme aynı baytları iki kez çekerdi.
            F10 cutover'ından sonra panel TEK tüketici; yükleme sorumluluğu hâlâ bu
            bileşende çünkü iki tarafı zaten indiriyor.
          */
          firstDocument={this._oldPdfDoc as PdfDocumentProxy | null}
          secondDocument={this._newPdfDoc as PdfDocumentProxy | null}
          /*
            `scanError` GEÇİRİLİYOR. Eskiden set ediliyor ve HİÇBİR YERDE çizilmiyordu —
            `scanPageIdx` ile aynı sınıf. Sonuç: gerçek hata (ör. pdfjs'in
            InvalidPDFException'ı, 415, ağ hatası) state'te duruyor, kullanıcı yalnızca
            genel "sayfa görüntüleri yüklenmedi" satırını görüyor ve SEBEBİNİ bilmiyor.
          */
          pageImageError={this.state.scanError}
          pageImagesLoading={this.state.scanLoading}
        />
      </div>
    );
  }

  private renderSummary() {
    const { result, currentChangeIndex, exportingPdf, exportError } = this.state;
    if (!result) return null;
    const isTr = this.props.locale === "tr";

    // Show message bar even without summary (e.g., MIME mismatch info, error context)
    if (!result.summary) {
      if (result.message) {
        return (
          <div
            style={{
              padding: "8px 16px",
              background: "#fff3cd",
              borderBottom: "1px solid #ffc107",
              fontSize: 12,
              fontFamily: "sans-serif",
              color: "#856404"
            }}
          >
            {result.message}
          </div>
        );
      }
      return null;
    }
    const s = result.summary;
    const changeIndices = this.getChangeIndices();
    const hasChanges = changeIndices.length > 0;
    const hasFormatChanges = result.formatChanges && result.formatChanges.length > 0;

    // File type icon based on MIME
    const mimeIcon = this.getMimeIcon(result.mimeType);

    // Format-specific info
    const formatInfo = this.getFormatSpecificInfo(result);

    const navBtnStyle: CSSProperties = {
      padding: "2px 10px",
      fontSize: 11,
      fontWeight: 600,
      border: "1px solid #ccc",
      borderRadius: 4,
      background: "#fff",
      cursor: hasChanges ? "pointer" : "not-allowed",
      opacity: hasChanges ? 1 : 0.4,
      fontFamily: "sans-serif",
      color: "#555"
    };

    return (
      <div
        style={{
          // Sprint 15 — Sticky summary bar so similarity / change counts stay visible while
          // scrolling long diffs. Position-sticky + top:0 keeps it pinned to the top of the
          // scroll container; zIndex 5 sits above the canvas overlay (which has none) but
          // below modals and tooltips. The opaque background prevents tokens scrolling
          // through it.
          position: "sticky",
          top: 0,
          zIndex: 5,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 16px",
          background: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
          fontSize: 13,
          fontFamily: "sans-serif",
          color: "#555",
          flexWrap: "wrap"
        }}
      >
        {/* File icon and document names */}
        <span style={{ fontSize: 16, lineHeight: 1 }}>{mimeIcon}</span>
        <span style={{ fontSize: 12, color: "#888" }}>
          {(result as any).oldDocumentName || result.documentName} v{result.oldVersion}
          {result.oldLanguage ? ` (${result.oldLanguage})` : ""}
        </span>
        <span style={{ color: "#aaa", fontSize: 14, fontWeight: 700 }}>&rarr;</span>
        <span style={{ fontSize: 12, color: "#333", fontWeight: 600 }}>
          {result.documentName} v{result.newVersion}
          {result.newLanguage ? ` (${result.newLanguage})` : ""}
        </span>

        <span style={{ color: "#ddd" }}>|</span>

        {/* Similarity badge */}
        {result.comparisonMode !== "collabora" && this.renderSimilarityBadge()}

        {/* Statistics mini progress bar */}
        {result.comparisonMode !== "collabora" && this.renderStatsBar()}

        {/* Change counts */}
        <span style={{ fontSize: 12 }}>
          {result.comparisonMode === "collabora"
            ? isTr
              ? "Degisiklikler asagida gosterilmektedir"
              : "Changes shown below"
            : `${s.totalChanges} ${isTr ? "degisiklik" : `change${s.totalChanges !== 1 ? "s" : ""}`}`}
        </span>
        {result.comparisonMode !== "collabora" && s.additions > 0 && (
          <span style={{ color: "#28a745", fontSize: 12 }}>+{s.additions}</span>
        )}
        {result.comparisonMode !== "collabora" && s.deletions > 0 && (
          <span style={{ color: "#d73a49", fontSize: 12 }}>-{s.deletions}</span>
        )}
        {result.comparisonMode !== "collabora" && s.modifications > 0 && (
          <span style={{ color: "#e36209", fontSize: 12 }}>~{s.modifications}</span>
        )}

        {/* v1'in önem çipleri KALDIRILDI (F10): `scanDiff.summary.severityBreakdown`'dan
            besleniyorlardı. v2 önem ve güven çiplerini kendi panelinde, üstelik İKİ AYRI
            eksen olarak gösteriyor — tek renge yıkmak "kritik ama şüpheli" ile "biçimsel
            ama kesin" arasını görünmez kılardı. */}

        {/* Format changes badge — BILGI, buton degil.
            Eskiden tiklanabilirdi (`sidebarTab: "format"`) ama arkasindaki liste
            `renderSidebar` ile birlikte hic cagrilmiyordu: gorunur, tiklanabilir ve
            HICBIR SEY YAPMAYAN bir buton. Bicim/tipografi diff'i planda kapsam disi,
            yani dogru duzeltme listeyi geri getirmek degil butonu buton olmaktan
            cikarmak. Sayi bilgi olarak duruyor. */}
        {hasFormatChanges && (
          <span
            style={{
              padding: "1px 6px",
              borderRadius: 8,
              background: "#e8eaf6",
              color: "#3f51b5",
              fontSize: 10,
              fontWeight: 600
            }}
            title={
              isTr
                ? "Bicim degisikligi sayisi (bicim diff'i bu surumde gosterilmiyor)"
                : "Formatting change count (formatting diff is not displayed in this version)"
            }
          >
            {result.formatChanges!.length} {isTr ? "format" : "format"}
          </span>
        )}

        {/* Format-specific info */}
        {formatInfo && (
          <>
            <span style={{ color: "#ddd" }}>|</span>
            <span style={{ fontSize: 11, color: "#888" }}>{formatInfo}</span>
          </>
        )}

        {/* Spacer */}
        <span style={{ flex: 1 }} />

        {/* Navigation: prev/next change */}
        {hasChanges && (
          <>
            <button
              type="button"
              style={navBtnStyle}
              onClick={() => this.navigateChange(-1)}
              title={isTr ? "Onceki degisiklik (Ctrl+P)" : "Previous change (Ctrl+P)"}
            >
              &#9650;
            </button>
            <span style={{ fontSize: 11, color: "#888", minWidth: 36, textAlign: "center" }}>
              {currentChangeIndex >= 0
                ? `${currentChangeIndex + 1}/${changeIndices.length}`
                : `—/${changeIndices.length}`}
            </span>
            <button
              type="button"
              style={navBtnStyle}
              onClick={() => this.navigateChange(1)}
              title={isTr ? "Sonraki degisiklik (Ctrl+N)" : "Next change (Ctrl+N)"}
            >
              &#9660;
            </button>
          </>
        )}

        {/* Rapor indirme — plan 0.4.
            `exportPdf` implementliydi ve HICBIR `onClick`'i yoktu: backend raporu
            (compare/as-file) saglam, uc calisiyor, buton hic cizilmemis. Plan bunu
            kapsam-disi notunda ayrica kaydediyor: yan yana PDF export kapsam disi ama
            "butonu 0.4'te geri geliyor" — mevcut HTML-print yolu kaliyor.

            Hata artik gorunur (bkz. exportPdf): eskiden `catch { }` sessizce yutuyordu
            ve kullanici icin bu "bastim, hicbir sey olmadi"ya esitti. */}
        <button
          type="button"
          disabled={exportingPdf}
          onClick={this.exportPdf}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 10px",
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid #d0d7de",
            borderRadius: 4,
            background: exportingPdf ? "#f6f8fa" : "#fff",
            color: exportingPdf ? "#8c959f" : "#24292f",
            cursor: exportingPdf ? "default" : "pointer"
          }}
          title={isTr ? "Karsilastirma raporunu indir" : "Download comparison report"}
        >
          {exportingPdf ? (isTr ? "Hazirlaniyor…" : "Preparing…") : isTr ? "Rapor indir" : "Download report"}
        </button>

        {exportError && (
          <span style={{ fontSize: 11, color: "#b3261e", fontWeight: 600 }} title={exportError}>
            {exportError}
          </span>
        )}

        {/* DOCX download — only for docxExport mode */}
        {result.comparisonMode === "docxExport" && (result as any)?.resultDocumentUrl && (
          <a
            href={(result as any).resultDocumentUrl}
            download="comparison-result.docx"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 10px",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid #c8e6c9",
              borderRadius: 4,
              background: "#e8f5e9",
              color: "#2e7d32",
              textDecoration: "none",
              cursor: "pointer"
            }}
            title={isTr ? "Karsilastirma DOCX indir" : "Download comparison DOCX"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2e7d32"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            DOCX
          </a>
        )}
      </div>
    );
  }

  /** Returns an appropriate file icon character for the MIME type. */
  private getMimeIcon(mimeType: string): string {
    if (!mimeType) return "\uD83D\uDCC4"; // generic file
    if (mimeType.startsWith("image/")) return "\uD83D\uDDBC"; // framed picture
    if (mimeType.includes("pdf")) return "\uD83D\uDCC4"; // page
    if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "\uD83D\uDCCA"; // bar chart
    if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "\uD83D\uDCFD"; // projector
    if (mimeType.includes("word") || mimeType.includes("document")) return "\uD83D\uDCC3"; // page with curl
    return "\uD83D\uDCC4";
  }

  /** Returns format-specific summary text based on comparison mode. */
  private getFormatSpecificInfo(result: ComparisonResult): string | null {
    const isTr = this.props.locale === "tr";
    const s = result.summary;
    if (!s) return null;

    switch (result.comparisonMode) {
      case "cellDiff": {
        const cells = (result as any)?.cellChanges?.length ?? s.totalChanges;
        return isTr ? `${cells} hücre değişti` : `${cells} cell${cells !== 1 ? "s" : ""} changed`;
      }
      case "pageDiff": {
        const pages = (result as any)?.pageChanges ?? [];
        const modifiedPages = pages.filter((p: any) => p.type === "modified").map((p: any) => p.pageNumber);
        if (modifiedPages.length > 0 && modifiedPages.length <= 5) {
          const pageList = modifiedPages.join(", ");
          return isTr
            ? `Sayfa ${pageList} değişti`
            : `Page${modifiedPages.length > 1 ? "s" : ""} ${pageList} differ${modifiedPages.length === 1 ? "s" : ""}`;
        }
        return isTr
          ? `${pages.length} sayfa etkilendi`
          : `${pages.length} page${pages.length !== 1 ? "s" : ""} affected`;
      }
      case "slideDiff": {
        const slides = (result as any)?.slideChanges ?? [];
        return isTr
          ? `${slides.length} slayt etkilendi`
          : `${slides.length} slide${slides.length !== 1 ? "s" : ""} affected`;
      }
      case "imageDiff": {
        const img = result.imageDiff;
        if (img) {
          const sim = `${img.similarityPercent.toFixed(1)}%`;
          return isTr ? `Benzerlik: ${sim}` : `Similarity: ${sim}`;
        }
        return null;
      }
      default:
        return null;
    }
  }

  // ── Render: Identical versions ──────────────────────────────────────────────

  private renderIdentical() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: "#28a745",
          fontFamily: "sans-serif"
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {this.props.locale === "tr" ? "Fark bulunamadı" : "No differences"}
        </span>
        <span style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
          {this.state.result?.message ??
            (this.props.locale === "tr" ? "Seçilen versiyonlar aynı." : "The selected versions are identical.")}
        </span>
      </div>
    );
  }

  // ── Render: PDF Page diff ────────────────────────────────────────────────

  private renderPageDiff() {
    const { result } = this.state;
    const pages: any[] = (result as any)?.pageChanges ?? [];
    const isTr = this.props.locale === "tr";

    if (pages.length === 0) return this.renderIdentical();

    const typeColor: Record<string, string> = { added: "#e6ffed", deleted: "#ffeef0", modified: "#fff8e1" };
    const typeBadge: Record<string, string> = { added: "#28a745", deleted: "#d73a49", modified: "#e36209" };
    const typeLabel: Record<string, string> = {
      added: isTr ? "Yeni Sayfa" : "New Page",
      deleted: isTr ? "Silinen Sayfa" : "Deleted Page",
      modified: isTr ? "Değişen Sayfa" : "Modified Page"
    };

    const lineStyle = (type: string): CSSProperties => ({
      padding: "1px 6px",
      fontFamily: "monospace",
      fontSize: 11,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minHeight: 18,
      lineHeight: "18px",
      background: type === "added" ? "#e6ffed" : type === "deleted" ? "#ffeef0" : "transparent",
      borderLeft:
        type === "added" ? "2px solid #28a745" : type === "deleted" ? "2px solid #d73a49" : "2px solid transparent"
    });

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif", fontSize: 12 }}>
        {pages.map((p: any, i: number) => (
          <div key={i} style={{ marginBottom: 8, border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: typeColor[p.type] || "#f8f9fa",
                borderBottom: "1px solid #e0e0e0"
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13 }}>
                {isTr ? "Sayfa" : "Page"} {p.pageNumber}
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "1px 8px",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  background: typeBadge[p.type] || "#888"
                }}
              >
                {typeLabel[p.type] || p.type}
              </span>
              {p.summary && (
                <span style={{ fontSize: 10, color: "#888" }}>
                  +{p.summary.additions} -{p.summary.deletions} ~{p.summary.modifications}
                </span>
              )}
            </div>
            {p.type === "modified" && p.chunks?.length > 0 ? (
              <div style={{ maxHeight: 200, overflow: "auto", padding: 4 }}>
                {p.chunks.map((chunk: any, ci: number) => (
                  <div key={ci} style={lineStyle(chunk.type)}>
                    {chunk.type === "deleted" ? chunk.oldContent : chunk.content}
                  </div>
                ))}
              </div>
            ) : p.type === "added" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#28a745" }}>{p.newText}</div>
            ) : p.type === "deleted" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#d73a49", textDecoration: "line-through" }}>
                {p.oldText}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  // ── Render: PDF Visual diff (diff-pdf output) ──────────────────────────

  private renderPdfVisualDiff() {
    const { result } = this.state;
    const diffBase64 = (result as any)?.diffPdfBase64;
    const isTr = this.props.locale === "tr";

    if (!diffBase64) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#888"
          }}
        >
          {isTr ? "Görsel diff oluşturulamadı." : "Visual diff could not be generated."}
        </div>
      );
    }

    const pdfUrl = `data:application/pdf;base64,${diffBase64}`;

    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            padding: "8px 12px",
            background: "#fff3cd",
            borderBottom: "1px solid #ffc107",
            fontSize: 12,
            color: "#856404"
          }}
        >
          {isTr
            ? "Kırmızı alanlar değişiklikleri gösterir (diff-pdf pixel karşılaştırması)."
            : "Red areas indicate changes (diff-pdf pixel comparison)."}
        </div>
        <iframe src={pdfUrl} style={{ flex: 1, border: "none", width: "100%" }} title="PDF Visual Diff" />
      </div>
    );
  }

  // ── Scan-aware compare: pdfjs canvas + bbox overlay (Diffchecker-style) ─

  private async loadPdfjs(): Promise<any> {
    if (this._pdfjsLib) return this._pdfjsLib;
    // `pdfjs-dist` main build uses `Map.prototype.getOrInsertComputed` (TC39 Stage 3, not
    // shipped in any browser yet → "getOrInsertComputed is not a function"). The legacy
    // build targets ES2017 + ships polyfills, so it runs everywhere.
    const pdfjs: any = await import("pdfjs-dist/legacy/build/pdf.mjs");
    const cdn = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;
    pdfjs.GlobalWorkerOptions.workerSrc = cdn;
    // eslint-disable-next-line no-console
    console.info("[DocumentCompare] pdfjs-dist (legacy)", pdfjs.version, "workerSrc=", cdn);
    this._pdfjsLib = pdfjs;
    return pdfjs;
  }

  private resetScanCanvasState = () => {
    this._oldPdfDoc = null;
    this._newPdfDoc = null;
    this._scanOldViewport = null;
    this._scanNewViewport = null;
    this._scanGlobalTargetWidth = null;
    this.setState({ scanPdfsLoaded: false, scanLoading: false, scanError: null });
  };

  private loadScanPdfs = async () => {
    const { result } = this.state;
    // Panel `compareV2` geldiginde ciziliyor, moda bakmadan — yukleme de ayni olcute
    // bagli olmak zorunda. Bkz. componentDidUpdate.
    if (!isCompareResultV2(result?.compareV2)) return;

    const leftId = this._overrideLeftVersionId ?? this.props.leftVersionId;
    const rightId = this._overrideRightVersionId ?? this.props.rightVersionId;
    if (!leftId || !rightId) {
      // SESSIZ vazgecme yok. `compare/render/{versionId}` ucu SURUM id'si istiyor;
      // karsilastirma belge id'siyle yapildiysa elimizde surum id'si olmuyor ve sayfa
      // goruntusu cizilemiyor. Eskiden burada sessizce `return` vardi ve kullanici
      // "sayfa goruntuleri yuklenmedi" gorup SEBEBINI bilmiyordu.
      const isTr = this.props.locale === "tr";
      this.setState({
        scanLoading: false,
        scanError: isTr
          ? "Sayfa görüntüsü için sürüm kimliği gerekiyor; karşılaştırma belge kimliğiyle yapılmış."
          : "Page images need version ids; this comparison was made with document ids."
      });
      return;
    }

    const myToken = ++this._scanLoadToken;
    const isTr: boolean = this.props.locale === "tr";
    this.setState({ scanLoading: true, scanError: null });

    try {
      const pdfjs = await this.loadPdfjs();
      const base = KuikaAppManager.GetBackendUrl().replace(/\/$/, "");
      const [oldResp, newResp] = await Promise.all([
        axios.get(`${base}/documentmanagement/compare/render/${leftId}`, { responseType: "arraybuffer" }),
        axios.get(`${base}/documentmanagement/compare/render/${rightId}`, { responseType: "arraybuffer" })
      ]);
      if (myToken !== this._scanLoadToken) return; // newer load superseded us

      // eslint-disable-next-line no-console
      console.info("[DocumentCompare] rendered pdf bytes:", oldResp.data?.byteLength, "/", newResp.data?.byteLength);

      // IKI TARAF BAGIMSIZ COZULUYOR.
      //
      // Eskiden ikisi birlikte `Promise.all` icinde pdfjs'e veriliyordu: BIR taraf bozuksa
      // digeri de goruntulenmiyordu ve hata hangi tarafin bozuk oldugunu SOYLEMIYORDU.
      // Karsilastirma urunu icin bu iki kayip birden — calisan tarafi da gizliyor ve
      // teshis edilemez bir mesaj biraliyor.
      //
      // Onden bir "PDF mi" kapisi YOK: pdfjs her iki tarafi da DENIYOR. Bayt incelemesi
      // yalnizca gercekten basarisiz olan tarafta, SEBEBI yazmak icin yapiliyor.
      const oldParse = await describeAndParsePdf(pdfjs, oldResp, isTr ? "Birinci belge" : "First document");
      const newParse = await describeAndParsePdf(pdfjs, newResp, isTr ? "İkinci belge" : "Second document");
      if (myToken !== this._scanLoadToken) return;

      this._oldPdfDoc = oldParse.doc;
      this._newPdfDoc = newParse.doc;

      const reasons: string[] = [oldParse.problem, newParse.problem].filter(
        (r: string | null): r is string => r !== null
      );

      if (oldParse.doc === null && newParse.doc === null) {
        // Iki taraf da cozulemedi: gosterilecek bir sey yok, sebepleri yaz.
        // eslint-disable-next-line no-console
        console.error("[DocumentCompare] page images unusable:", reasons.join(" | "));
        this.setState({ scanLoading: false, scanError: reasons.join("  ") });
        return;
      }

      if (reasons.length > 0) {
        // TEK taraf bozuk. Saglam tarafi YINE de ciziyoruz.
        //
        // `DocumentPane` zaten `document={null}` durumunu karsiliyor ve `hasViewer` bir
        // taraf varsa true donuyor — yani gorunutuleyici bastan tek tarafli calisacak
        // sekilde yazilmis. Eskiden ikisi `Promise.all` icinde birlikte cozuldugu icin bu
        // yetenek hic kullanilamiyordu: bir taraftaki hata SAGLAM tarafi da goturuyordu.
        // eslint-disable-next-line no-console
        console.warn("[DocumentCompare] one side unusable:", reasons.join(" | "));
      }

      const oldDoc = oldParse.doc;
      const newDoc = newParse.doc;

      // eslint-disable-next-line no-console
      console.info("[DocumentCompare] pdfjs docs loaded — pages:", oldDoc.numPages, "/", newDoc.numPages);

      // Measure page-1 native widths so we can normalize both sides to the larger one.
      // Without this, a DOCX→LibreOffice→PDF (A4 ≈ 595pt) renders visibly smaller than
      // a native PDF whose intrinsic width is bigger, even at the same `scanZoom`.
      try {
        // Tek taraf cozulmus olabilir; olcum eldeki taraflarla yapiliyor.
        const [oldP1, newP1] = await Promise.all([
          oldDoc ? oldDoc.getPage(1) : Promise.resolve(null),
          newDoc ? newDoc.getPage(1) : Promise.resolve(null)
        ]);
        if (myToken !== this._scanLoadToken) return;
        const widths: number[] = [oldP1, newP1]
          .filter((p: any): boolean => p !== null)
          .map((p: any): number => p.getViewport({ scale: 1 }).width);
        const oldNativeW = oldP1 ? oldP1.getViewport({ scale: 1 }).width : null;
        const newNativeW = newP1 ? newP1.getViewport({ scale: 1 }).width : null;
        this._scanGlobalTargetWidth = widths.length > 0 ? Math.max(...widths) : null;
        // eslint-disable-next-line no-console
        console.info(
          "[DocumentCompare] page-1 native widths (pt): old=",
          oldNativeW,
          "new=",
          newNativeW,
          "→ targetW=",
          this._scanGlobalTargetWidth
        );
      } catch (e) {
        // Non-fatal: fall back to no normalization (previous behavior).
        // eslint-disable-next-line no-console
        console.warn("[DocumentCompare] could not measure native widths, skipping normalization:", e);
        this._scanGlobalTargetWidth = null;
      }

      // Initial render is triggered from componentDidUpdate on the false → true transition
      // of scanPdfsLoaded — a single deterministic tetik, no race with ref callbacks.
      this.setState({
        scanPdfsLoaded: true,
        scanLoading: false,
        // Bozuk taraf varsa sebebi KORUYORUZ: saglam taraf ciziliyor ama kullanici
        // digerinde ne oldugunu bilmek zorunda. Sessizce yarim gostermek, eksigi
        // gizlemek olurdu.
        scanError: reasons.length > 0 ? reasons.join("  ") : null
      });
    } catch (e: any) {
      if (myToken !== this._scanLoadToken) return;
      // eslint-disable-next-line no-console
      console.error("[DocumentCompare] loadScanPdfs failed:", e);

      // Hata mesaji NE GELDIGINI soyluyor. pdfjs'in `InvalidPDFException`i tek basina
      // hangi tarafin ne dondurdugu hakkinda hicbir sey soylemiyor; bu tesih onden bir
      // kapi koymak yerine GERCEKLESEN hatayi aciklamak icin.
      let detail = "";
      const isPdfError: boolean = /invalidpdf|invalid pdf/i.test(String(e?.name ?? "") + String(e?.message ?? ""));
      if (isPdfError) {
        detail =
          " " +
          (isTr
            ? "Sunucu PDF olmayan bir yanıt döndürdü. Bu genellikle şu üçünden biri: " +
              "karşılaştırma ucu bu uygulamada üretilmemiş (SPA index.html'i dönüyor), " +
              "belgenin biçimi çevrilemiyor, ya da bir vekil hata sayfası ekliyor. " +
              "Tarayıcı ağ sekmesinde compare/render isteğinin yanıtına bakmak ayırt eder."
            : "The server returned a non-PDF response. Usually one of three: the compare " +
              "render endpoint was not generated into this app (the SPA index.html comes " +
              "back), the document format cannot be converted, or a proxy injected an " +
              "error page. The network tab's compare/render response tells them apart.");
      }

      const msg = e?.response?.data?.message ?? e?.message ?? String(e);
      this.setState({ scanLoading: false, scanError: msg + detail });
    }
  };

  private onScanLeftScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (this._isScrollSyncing) return;
    const src = e.currentTarget;
    const dst = this._rightPanelRef;
    if (!dst) return;
    this._isScrollSyncing = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => {
      this._isScrollSyncing = false;
    });
  };

  private onScanRightScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (this._isScrollSyncing) return;
    const src = e.currentTarget;
    const dst = this._leftPanelRef;
    if (!dst) return;
    this._isScrollSyncing = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => {
      this._isScrollSyncing = false;
    });
  };

  /** FAZ 6B — char-level diff popover handlers. Show the popover when the user hovers
   *  a "modified" token rect that has charDiff segments populated by the backend.
   *  Hide when the cursor leaves. The popover follows the mouse so it doesn't get
   *  clipped against the panel edges. */

  private getScanPageCount(): number {
    const o = this._oldPdfDoc?.numPages ?? 0;
    const n = this._newPdfDoc?.numPages ?? 0;
    return Math.max(o, n);
  }

  // ── Render: Scan PDF diff (OCR Enrichment canonical JSON) ───────────────

  /** FAZ 6B — render the per-character delete/equal/insert breakdown for a hovered
   *  modified token. Equal segments stay normal-weight; deleted characters are red with
   *  strikethrough; inserted characters are green and bold. Two rows shown: the source
   *  word with its deletions, and the target word with its insertions. */

  // ── Render: Slide diff (PPTX) ────────────────────────────────────────────

  private renderSlideDiff() {
    const { result } = this.state;
    const slides: any[] = (result as any)?.slideChanges ?? [];
    const isTr = this.props.locale === "tr";

    if (slides.length === 0) return this.renderIdentical();

    const typeColor: Record<string, string> = { added: "#e6ffed", deleted: "#ffeef0", modified: "#fff8e1" };
    const typeBadge: Record<string, string> = { added: "#28a745", deleted: "#d73a49", modified: "#e36209" };
    const typeLabel: Record<string, string> = {
      added: isTr ? "Yeni Slayt" : "New Slide",
      deleted: isTr ? "Silinen Slayt" : "Deleted Slide",
      modified: isTr ? "Değişen Slayt" : "Modified Slide"
    };

    const lineStyle = (type: string): CSSProperties => ({
      padding: "1px 6px",
      fontFamily: "monospace",
      fontSize: 11,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minHeight: 18,
      lineHeight: "18px",
      background: type === "added" ? "#e6ffed" : type === "deleted" ? "#ffeef0" : "transparent",
      borderLeft:
        type === "added" ? "2px solid #28a745" : type === "deleted" ? "2px solid #d73a49" : "2px solid transparent"
    });

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif", fontSize: 12 }}>
        {slides.map((s: any, i: number) => (
          <div key={i} style={{ marginBottom: 8, border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
            {/* Slide header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: typeColor[s.type] || "#f8f9fa",
                borderBottom: "1px solid #e0e0e0"
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13 }}>
                {isTr ? "Slayt" : "Slide"} {s.slideNumber}
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "1px 8px",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  background: typeBadge[s.type] || "#888"
                }}
              >
                {typeLabel[s.type] || s.type}
              </span>
            </div>

            {/* Slide content diff */}
            {s.type === "modified" && s.chunks?.length > 0 ? (
              <div style={{ maxHeight: 200, overflow: "auto", padding: 4 }}>
                {s.chunks.map((chunk: any, ci: number) => (
                  <div key={ci} style={lineStyle(chunk.type)}>
                    {chunk.type === "deleted" ? chunk.oldContent : chunk.content}
                  </div>
                ))}
              </div>
            ) : s.type === "added" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#28a745" }}>{s.newText}</div>
            ) : s.type === "deleted" ? (
              <div style={{ padding: "8px 12px", fontSize: 11, color: "#d73a49", textDecoration: "line-through" }}>
                {s.oldText}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  // ── Render: Cell diff (XLSX) ─────────────────────────────────────────────

  private renderCellDiff() {
    const { result } = this.state;
    const changes: any[] = (result as any)?.cellChanges ?? [];
    const isTr = this.props.locale === "tr";

    if (changes.length === 0) {
      return this.renderIdentical();
    }

    const typeColor: Record<string, string> = {
      added: "#e6ffed",
      deleted: "#ffeef0",
      modified: "#fff8e1"
    };
    const typeLabel: Record<string, string> = {
      added: isTr ? "Eklenen" : "Added",
      deleted: isTr ? "Silinen" : "Deleted",
      modified: isTr ? "Değişen" : "Modified"
    };
    const typeBadge: Record<string, string> = {
      added: "#28a745",
      deleted: "#d73a49",
      modified: "#e36209"
    };

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif", fontSize: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8f9fa", position: "sticky", top: 0, zIndex: 1 }}>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Hücre" : "Cell"}
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Durum" : "Status"}
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Eski Değer" : "Old Value"}
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #dee2e6",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#495057"
                }}
              >
                {isTr ? "Yeni Değer" : "New Value"}
              </th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c: any, i: number) => (
              <tr key={i} style={{ background: typeColor[c.type] || "transparent" }}>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                    fontFamily: "monospace",
                    fontWeight: 600
                  }}
                >
                  {c.cell}
                </td>
                <td style={{ padding: "6px 12px", borderBottom: "1px solid #eee" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "1px 8px",
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fff",
                      background: typeBadge[c.type] || "#888"
                    }}
                  >
                    {typeLabel[c.type] || c.type}
                  </span>
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                    color: c.type === "deleted" || c.type === "modified" ? "#d73a49" : "#888",
                    textDecoration: c.type === "modified" ? "line-through" : "none"
                  }}
                >
                  {c.oldValue ?? "—"}
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    borderBottom: "1px solid #eee",
                    color: c.type === "added" || c.type === "modified" ? "#28a745" : "#888",
                    fontWeight: c.type === "modified" ? 600 : 400
                  }}
                >
                  {c.newValue ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Render: Image diff ────────────────────────────────────────────────────

  private renderImageDiff() {
    const { result, oldImageBlobUrl, newImageBlobUrl } = this.state;
    const isTr = this.props.locale === "tr";
    const img = result?.imageDiff;

    if (!img) return this.renderUnsupported();

    const similarity = img.similarityPercent;
    // Color: green if similar, yellow if moderate, red if very different
    const barColor = similarity >= 90 ? "#28a745" : similarity >= 50 ? "#ff9800" : "#d73a49";

    return (
      <div style={{ width: "100%", height: "100%", overflow: "auto", fontFamily: "sans-serif" }}>
        {/* Similarity bar */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{isTr ? "Benzerlik" : "Similarity"}</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: barColor }}>{similarity.toFixed(1)}%</span>
          </div>
          <div
            style={{
              width: "100%",
              height: 8,
              background: "#eee",
              borderRadius: 4,
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: `${Math.min(100, similarity)}%`,
                height: "100%",
                background: barColor,
                borderRadius: 4,
                transition: "width 0.5s ease"
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 8, fontSize: 11, color: "#888" }}>
            <span>
              {isTr ? "Eski" : "Old"}: {img.oldWidth} x {img.oldHeight}px
            </span>
            <span>
              {isTr ? "Yeni" : "New"}: {img.newWidth} x {img.newHeight}px
            </span>
            {!img.dimensionsMatch && (
              <span style={{ color: "#e36209" }}>{isTr ? "Boyut farklı" : "Dimensions differ"}</span>
            )}
          </div>
        </div>

        {/* Side-by-side image preview */}
        <div style={{ display: "flex", width: "100%", flex: 1 }}>
          <div style={{ flex: 1, padding: 12, borderRight: "1px solid #e0e0e0", textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>
              {isTr ? "Eski Versiyon" : "Old Version"} (v{result!.oldVersion})
            </div>
            {oldImageBlobUrl ? (
              <img
                src={oldImageBlobUrl}
                alt="Old version"
                style={{ maxWidth: "100%", maxHeight: 400, border: "1px solid #ddd", borderRadius: 4 }}
              />
            ) : (
              <div style={{ padding: 40, color: "#aaa", fontSize: 12 }}>{isTr ? "Yükleniyor..." : "Loading..."}</div>
            )}
          </div>
          <div style={{ flex: 1, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>
              {isTr ? "Yeni Versiyon" : "New Version"} (v{result!.newVersion})
            </div>
            {newImageBlobUrl ? (
              <img
                src={newImageBlobUrl}
                alt="New version"
                style={{ maxWidth: "100%", maxHeight: 400, border: "1px solid #ddd", borderRadius: 4 }}
              />
            ) : (
              <div style={{ padding: 40, color: "#aaa", fontSize: 12 }}>{isTr ? "Yükleniyor..." : "Loading..."}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Error (decryption failure, etc.) ───────────────────────────────

  private renderError() {
    const { result } = this.state;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: "#c00",
          fontFamily: "sans-serif"
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {this.props.locale === "tr" ? "Karşılaştırma hatası" : "Comparison error"}
        </span>
        <span style={{ fontSize: 13, color: "#888", marginTop: 8, textAlign: "center", maxWidth: 400 }}>
          {result?.message ?? (this.props.locale === "tr" ? "Bilinmeyen hata." : "Unknown error.")}
        </span>
      </div>
    );
  }

  // ── Render: Unsupported ───────────────────────────────────────────────────

  private renderUnsupported() {
    const { result } = this.state;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: "#888",
          fontFamily: "sans-serif"
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 16 }}>&#128196;</span>
        <span style={{ fontSize: 15 }}>
          {result?.message ??
            (this.props.locale === "tr"
              ? "Bu format için karşılaştırma desteklenmiyor."
              : "Comparison not available for this format.")}
        </span>
        <span style={{ fontSize: 13, color: "#aaa", marginTop: 8 }}>{result?.mimeType}</span>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  render(): ReactNode {
    const { style } = this.props;
    const { loading, error, result } = this.state;
    const leftVersionId = this._overrideLeftVersionId || this.props.leftVersionId;
    const rightVersionId = this._overrideRightVersionId || this.props.rightVersionId;

    if ((window as any)?.kuika?.isDesignTime) {
      return this.renderPlaceholder();
    }

    if (loading) {
      return (
        <div
          className={this.props.className}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            fontFamily: "sans-serif",
            ...style
          }}
        >
          <span>{this.props.locale === "tr" ? "Versiyonlar karşılaştırılıyor..." : "Comparing versions..."}</span>
        </div>
      );
    }

    if (error) {
      return (
        <div
          className={this.props.className}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            color: "#c00",
            fontFamily: "sans-serif",
            ...style
          }}
        >
          <span>
            {this.props.locale === "tr" ? "Karşılaştırma hatası: " : "Comparison error: "}
            {error}
          </span>
        </div>
      );
    }

    if (!result) return this.renderPlaceholder();

    return (
      <div
        className={this.props.className}
        style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 800, ...style }}
      >
        {this.renderSummary()}
        <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
          {/*
            MOD GÖVDESİ boşsa SARMALAYICI DA ÇİZİLMİYOR.

            `flex: "1 1 40%"` gövde hiçbir şey çizmediğinde bile payını alıyordu ve v2
            paneli sağa sıkışıp sayfalar kesiliyordu. scanPdfDiff'te gövde artık null
            (panel tek yerde çiziliyor), yani o boş div ekranın yarısını yiyordu.
          */}
          {((): ReactNode => {
            const modeBody: ReactNode =
              result.comparisonMode === "identical"
                ? this.renderIdentical()
                : result.comparisonMode === "error"
                  ? this.renderError()
                  : result.comparisonMode === "imageDiff"
                    ? this.renderImageDiff()
                    : result.comparisonMode === "pageDiff"
                      ? this.renderPageDiff()
                      : result.comparisonMode === "pdfVisualDiff"
                        ? this.renderPdfVisualDiff()
                        : result.comparisonMode === "scanPdfDiff"
                          ? // scanPdfDiff'in kendi govdesi YOK: v2 paneli asagida kosulsuz
                            // ciziliyor. `renderScanCompare` yalnizca v2 HIC gelmediginde
                            // hata gostermek icin cagriliyor; v2 varsa null donuyor ve panel
                            // tek yerde ciziliyor (eskiden IKI KEZ cizilyordu).
                            this.renderScanCompare()
                          : result.comparisonMode === "cellDiff"
                            ? this.renderCellDiff()
                            : result.comparisonMode === "slideDiff"
                              ? this.renderSlideDiff()
                              : result.comparisonMode === "collabora"
                                ? this.state.settings.collabora.enabled
                                  ? this.renderCollabora()
                                  : this.renderUnsupported()
                                : result.comparisonMode === "docxExport"
                                  ? this.renderDocxExportCompare()
                                  : result.comparisonMode === "textDiff"
                                    ? this.renderTextDiff()
                                    : this.renderUnsupported();
            if (modeBody === null || modeBody === undefined || modeBody === false) return null;
            return (
              <div
                style={{
                  // v2 de varsa gövde daralıyor: sayfa görüntüsü ikincil. v2 yoksa tüm
                  // genişliği alıyor.
                  flex: isCompareResultV2(result.compareV2) ? "1 1 40%" : 1,
                  overflow: "hidden",
                  minWidth: 0
                }}
              >
                {modeBody}
              </div>
            );
          })()}
          {this.renderCompareV2Panel()}
        </div>
      </div>
    );
  }
}

const DocumentCompare = withCommonEvents(DocumentCompareInner);
export { DocumentCompare };

/**
 * Sayfa görüntüleri YÜKLENMELİ mi.
 *
 * <p><b>Neden ayrı ve dışa açık bir fonksiyon.</b> Bu koşulun kendisi bir hataydı ve
 * kullanıcıya "hangi modu seçersem seçeyim sayfa görüntüleri yüklenmedi" olarak ulaştı.
 * Koşul `comparisonMode === "scanPdfDiff"` idi, ama v2 paneli mod yönlendirmesinin YANINDA
 * koşulsuz çiziliyor ve yalnızca `compareV2`nin varlığına bakıyor. İki koşulun ayrı olması
 * demek: panel açık, PDF yok.</p>
 *
 * <p>Bileşenin içindeyken bu koşul test edilemiyordu — bütün sınıfı mount etmek pdfjs,
 * axios ve KuikaAppManager istiyor. Dışarı çıkarılınca koşul kendi başına çivilenebiliyor,
 * ve hata koşulun kendisi olduğu için doğru yer burası.</p>
 */
/**
 * Bir tarafın sayfa görüntüsü yanıtını çözer; başarısızsa NE GELDİĞİNİ anlatır.
 *
 * <p>Neden ayrı bir fonksiyon ve neden export: pdfjs'in
 * <code>InvalidPDFException: Invalid PDF structure</code> mesajı tek başına hiçbir şey
 * söylemiyor — hangi taraf, kaç bayt, hangi content-type, ilk baytlar ne. Bu hatanın teşhisi
 * tam bu eksiklik yüzünden saatler aldı. Artık sebep yanıtın KENDİSİNDEN okunuyor, yani bir
 * daha tahmin etmek gerekmiyor. Saf fonksiyon olduğu için test edilebiliyor.</p>
 *
 * <p>Bu bir "PDF mi" KAPISI değil: pdfjs her zaman deniyor. Baytlara yalnızca pdfjs gerçekten
 * başarısız olduğunda, sebebi yazmak için bakılıyor. Daha önce buraya konan önden yargılayan
 * bir kapı yanlış pozitif üretip çalışan bir akışı kapatmıştı.</p>
 */
export interface PdfSideParse {
  /** Çözülen pdfjs dokümanı; başarısızsa null. */
  doc: any | null;
  /** Kullanıcıya gösterilecek sebep; başarı hâlinde null. */
  problem: string | null;
}

export async function describeAndParsePdf(
  pdfjs: any,
  resp: { data?: unknown; status?: number; headers?: any } | undefined,
  sideLabel: string
): Promise<PdfSideParse> {
  const bytes: Uint8Array = responseToBytes(resp?.data);
  try {
    const doc: any = await pdfjs.getDocument({ data: bytes }).promise;
    return { doc, problem: null };
  } catch (e: any) {
    return { doc: null, problem: `${sideLabel}: ${explainUnusablePdf(resp, bytes, e)}` };
  }
}

/**
 * axios gövdesini baytlara çevirir.
 *
 * <p><code>resp.data</code> HER ZAMAN <code>ArrayBuffer</code> değil: bir interceptor,
 * <code>transformResponse</code> ya da sunucunun content-type'ı onu string'e çevirebiliyor.
 * Bir string üzerinde <code>new Uint8Array(...)</code> uzunluğu 0 olan bir dizi verir — bu
 * tam olarak daha önce yanlış pozitif üreten tuzaktı, o yüzden tür burada ayrıştırılıyor.</p>
 */
export function responseToBytes(data: unknown): Uint8Array {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (typeof data === "string") {
    const out: Uint8Array = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) out[i] = data.charCodeAt(i) & 0xff;
    return out;
  }
  return new Uint8Array(0);
}

/** Yanıtın gözlemlenebilir her ayrıntısını tek cümlede anlatır. */
export function explainUnusablePdf(
  resp: { status?: number; headers?: any } | undefined,
  bytes: Uint8Array,
  error: any
): string {
  const headers: any = resp?.headers;
  let contentType: string = "(yok)";
  if (headers) {
    const viaGetter: unknown = typeof headers.get === "function" ? headers.get("content-type") : undefined;
    const viaIndex: unknown = headers["content-type"];
    if (typeof viaGetter === "string") contentType = viaGetter;
    else if (typeof viaIndex === "string") contentType = viaIndex;
  }

  const hex: string = Array.from(bytes.slice(0, 12))
    .map((b: number): string => b.toString(16).padStart(2, "0"))
    .join(" ");
  const ascii: string = Array.from(bytes.slice(0, 40))
    .map((b: number): string => (b >= 32 && b < 127 ? String.fromCharCode(b) : "."))
    .join("");

  let guess: string;
  if (bytes.length === 0) guess = "Gövde boş.";
  else if (ascii.startsWith("<")) {
    guess =
      "Gövde HTML — sunucu PDF değil bir sayfa döndürdü (uç bu uygulamada üretilmemiş ya da araya bir vekil girdi).";
  } else if (ascii.startsWith("{") || ascii.startsWith("[")) {
    guess = "Gövde JSON — sunucu bir hata nesnesi döndürdü.";
  } else if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
    guess = "Gövde ZIP/DOCX — dönüşüm adımı atlanmış, belge olduğu gibi gelmiş.";
  } else if (!(bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46)) {
    guess = "Gövde PDF imzasıyla başlamıyor.";
  } else {
    guess = "PDF imzası var ama yapısı bozuk — içerik kısmi ya da kesilmiş.";
  }

  const pdfjsPart: string = `${String(error?.name ?? "")} ${String(error?.message ?? error)}`.trim();
  return (
    `sayfa görüntüsü çözülemedi (HTTP ${resp?.status ?? "?"}, content-type ${contentType}, ` +
    `${bytes.length} bayt, ilk baytlar ${hex || "(yok)"}, "${ascii}"). ${guess} pdfjs: ${pdfjsPart}`
  );
}

export function shouldLoadPageImages(state: {
  hasCompareV2: boolean;
  loading: boolean;
  error: string | null;
  loaded: boolean;
}): boolean {
  // Panelin çizildiği ölçütle AYNI: v2 sonucu varsa sayfa görüntüsü de gerekiyor.
  if (!state.hasCompareV2) return false;
  // Tekrar denememek için üç bayrak; `scheduleCompare` her yeni karşılaştırmada sıfırlıyor.
  return !state.loading && !state.error && !state.loaded;
}
